import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import {
  Checkbox,
  Chip,
  Text,
  PopoverMenu,
  PopoverMenuItem,
  SearchField,
  useFormField,
  usePopoverMenu,
  useTooltip,
  Button,
} from '@frontend/design-system';

export type FaxNumberOption = {
  locationPhone: string;
  displayName?: string;
};

type SmsTagsFilterPopoverContentProps = {
  faxNumbers: FaxNumberOption[];
  selectedFaxNumbers: string[];
  selectFaxNumbers: (faxNumbers: string[]) => void;
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getMenuProps'];
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getItemProps'];
};

export const FaxNumbersSelectPopover = ({
  faxNumbers,
  selectFaxNumbers,
  selectedFaxNumbers,
  getMenuProps,
  getItemProps,
}: SmsTagsFilterPopoverContentProps) => {
  const { t } = useTranslation('fax');
  const searchFieldProps = useFormField({ type: 'text' });

  const filteredFaxNumbers = searchFieldProps.value
    ? faxNumbers.filter(
        ({ locationPhone, displayName }) =>
          locationPhone.includes(searchFieldProps.value) ||
          displayName?.toLowerCase().includes(searchFieldProps.value.toLowerCase())
      )
    : faxNumbers;

  return (
    <PopoverMenu {...getMenuProps()}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          maxWidth: 220,
          paddingTop: theme.spacing(1),
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <SearchField
          {...searchFieldProps}
          name='fax-number-search'
          onKeyDownCapture={(e) => {
            e.stopPropagation();
          }}
        />
      </PopoverMenuItem>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 1,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='secondary'
          css={{ fontWeight: 'bold', color: theme.colors.primary50, border: 'none' }}
          onClick={() => selectFaxNumbers(filteredFaxNumbers.map(({ locationPhone }) => locationPhone))}
          disabled={!filteredFaxNumbers.length}
        >
          {t('Select all')}
        </Button>
        <Button
          variant='secondary'
          css={{
            fontWeight: 'bold',
            color: theme.colors.primary50,
            border: 'none',
          }}
          onClick={() => {
            selectFaxNumbers([]);
          }}
          disabled={!selectedFaxNumbers?.length}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div css={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 240 }}>
        {!filteredFaxNumbers.length ? (
          <Text textAlign='center' css={{ paddingTop: theme.spacing(1) }}>
            No matching numbers
          </Text>
        ) : (
          filteredFaxNumbers.map(({ locationPhone: number, displayName }, i) => {
            return (
              <PopoverMenuItem
                key={number}
                as='span'
                {...getItemProps({
                  index: i + 2,
                  disableCloseOnSelect: true,
                  onClick: () => {
                    selectFaxNumbers([number]);
                  },
                })}
                css={{
                  width: '100%',
                  height: 'auto',
                  padding: theme.spacing(1, 2),
                  cursor: 'pointer',
                  svg: { fill: theme.colors.white },
                }}
              >
                <Checkbox
                  id={number}
                  value={!!selectedFaxNumbers?.includes(number)}
                  onChange={() => {}}
                  onFocus={() => {}}
                  onBlur={() => {}}
                  aria-invalid={false}
                  active={false}
                  error={false}
                  name={number}
                />
                <FaxNumberChip locationPhone={number} displayName={displayName} />
              </PopoverMenuItem>
            );
          })
        )}
      </div>
    </PopoverMenu>
  );
};

const FaxNumberChip = ({ locationPhone, displayName }: FaxNumberOption) => {
  const {
    Tooltip: FaxNumberTooltip,
    triggerProps: faxNumberTooltipTriggerProps,
    tooltipProps: faxNumberTooltipProps,
  } = useTooltip({
    trigger: 'hover',
    placement: 'bottom',
    offset: 8,
  });

  const formattedNumber = formatPhoneNumber(locationPhone, true);
  const chipLabel = displayName ? `# ${displayName}` : `# ${formattedNumber}`;

  return (
    <>
      <Chip.Tag
        variant='neutral'
        css={{
          fontSize: theme.fontSize(12),
          color: theme.colorsV2.black(60),
          backgroundColor: theme.colorsV2.black(10),
          maxWidth: 180,
        }}
        {...faxNumberTooltipTriggerProps}
      >
        {chipLabel}
      </Chip.Tag>
      <FaxNumberTooltip {...faxNumberTooltipProps}>
        {displayName ? (
          <>
            {displayName}
            <br />
            {formattedNumber}
          </>
        ) : (
          formattedNumber
        )}
      </FaxNumberTooltip>
    </>
  );
};
